import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center px-4">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);

  const relatedArticles = articles
    .filter(
      (a) =>
        a.id !== article.id &&
        (a.categorySlug === article.categorySlug ||
          a.tags.some((t) => article.tags.includes(t))),
    )
    .slice(0, 4);

  const hotBadge: Record<string, { text: string; className: string }> = {
    explosive: { text: '爆炸性新闻', className: 'bg-primary-500 text-background-50' },
    hot: { text: '热门', className: 'bg-accent-500 text-background-50' },
    new: { text: '最新', className: 'bg-secondary-500 text-background-50' },
  };
  const badge = hotBadge[article.hotLevel];

  const categoryImage = article.imageUrl;

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.jinrichigua.com/',
      },
      ...(category
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: category.name,
              item: `https://www.jinrichigua.com/category/${category.slug}`,
            },
          ]
        : []),
      {
        '@type': 'ListItem',
        position: category ? 3 : 2,
        name: article.title,
        item: `https://www.jinrichigua.com/article/${article.slug}`,
      },
    ],
  };

  const articleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '今日吃瓜',
      url: 'https://www.jinrichigua.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.jinrichigua.com/article/${article.slug}`,
    },
    articleSection: article.category,
    keywords: article.tags.join(','),
  };

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Hero Banner */}
        <section className="relative w-full h-[350px] md:h-[450px] overflow-hidden">
          <img
            src={categoryImage}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70"></div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-10 md:pb-14">
            <div className="max-w-4xl mx-auto">
              {/* Breadcrumb */}
              <nav className="flex items-center flex-wrap gap-2 text-xs text-white/70 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-white transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line"></i>
                {category && (
                  <>
                    <Link to={`/category/${category.slug}`} className="hover:text-white transition-colors cursor-pointer">
                      {category.name}
                    </Link>
                    <i className="ri-arrow-right-s-line"></i>
                  </>
                )}
                <span className="text-white/90 truncate max-w-[200px] md:max-w-none">{article.title}</span>
              </nav>

              <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-3 ${badge.className}`}>
                {badge.text}
              </span>

              <h1 className="text-2xl md:text-4xl font-heading text-white leading-tight mb-3 max-w-3xl">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-white/60 flex-wrap">
                <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
                {category && (
                  <span>
                    <i className="ri-folder-line mr-1"></i>{category.name}
                  </span>
                )}
                <span>
                  <i className="ri-fire-line mr-1"></i>{article.heat.toLocaleString()} 热度
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            {/* Article body */}
            <div
              className="prose prose-base max-w-none text-foreground-800
                [&>p]:text-sm md:[&>p]:text-base [&>p]:leading-relaxed [&>p]:mb-5 [&>p]:text-foreground-700
                [&>h2]:text-xl md:[&>h2]:text-2xl [&>h2]:font-heading [&>h2]:text-foreground-900 [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:pb-2 [&>h2]:border-b [&>h2]:border-background-200
                [&>h3]:text-lg [&>h3]:font-heading [&>h3]:text-foreground-900 [&>h3]:mt-8 [&>h3]:mb-3
                "
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-xs text-foreground-500 mr-1">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Share */}
            <div className="mt-6 flex items-center gap-3">
              <span className="text-xs text-foreground-500">分享到：</span>
              <button
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="分享到微博"
              >
                <i className="ri-weibo-line"></i>
              </button>
              <button
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="分享到微信"
              >
                <i className="ri-wechat-line"></i>
              </button>
              <button
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="复制链接"
              >
                <i className="ri-link"></i>
              </button>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Back to top */}
        <div className="py-8 px-4 md:px-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页继续吃瓜
          </Link>
        </div>
      </main>

      <Footer />

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
    </>
  );
}